/**
 * Pinia store for the superadmin analytics dashboard.
 *
 * Persists the admin session token in localStorage under 'pfAdminToken'.
 * All analytics API calls attach `Authorization: SuperAdmin <token>`.
 */
import { defineStore } from 'pinia';
import axios from '@/axios';

const ADMIN_TOKEN_KEY = 'pfAdminToken';
const ADMIN_EMAIL_KEY = 'pfAdminEmail';

export const useAdminStore = defineStore('admin', {
  state: () => ({
    sessionToken: localStorage.getItem(ADMIN_TOKEN_KEY) || null,
    adminEmail: localStorage.getItem(ADMIN_EMAIL_KEY) || null,
    // data caches
    overview: null,
    users: null,
    usersList: null,
    searches: null,
    system: null,
    clientErrors: null,
    feedback: null,
    alerts: null,
    traffic: null,
    engagement: null,
    patterns: null,
    retention: null,
    kiwiQuota: null,
    searchLog: null,
    anonymousActivity: null,
    funnel: null,
    bookings: null,
    loading: false,
    error: null,
  }),

  getters: {
    isAdminAuthenticated: (state) => !!state.sessionToken,
    authHeader: (state) => ({
      Authorization: state.sessionToken ? `SuperAdmin ${state.sessionToken}` : '',
    }),
  },

  actions: {
    // ------------------------------------------------------------------
    // Auth
    // ------------------------------------------------------------------
    async requestLogin(email) {
      try {
        await axios.post('/analytics/admin/request-login/', { email });
        return { success: true };
      } catch (err) {
        return {
          success: false,
          error: err.response?.data?.error || 'Failed to send login email.',
        };
      }
    },

    async verifyToken(token) {
      try {
        const { data } = await axios.post('/analytics/admin/verify-token/', { token });
        this.sessionToken = data.session_token;
        this.adminEmail = data.email;
        localStorage.setItem(ADMIN_TOKEN_KEY, data.session_token);
        localStorage.setItem(ADMIN_EMAIL_KEY, data.email);
        return { success: true };
      } catch (err) {
        return {
          success: false,
          error: err.response?.data?.error || 'Invalid or expired token.',
        };
      }
    },

    async logout() {
      try {
        await axios.post('/analytics/admin/logout/', {}, { headers: this.authHeader });
      } catch (_) {
        // best-effort
      }
      this.sessionToken = null;
      this.adminEmail = null;
      this.overview = null;
      this.users = null;
      this.usersList = null;
      this.searches = null;
      this.system = null;
      this.clientErrors = null;
      this.feedback = null;
      this.alerts = null;
      this.traffic = null;
      this.engagement = null;
      this.patterns = null;
      this.retention = null;
      this.kiwiQuota = null;
      this.searchLog = null;
      this.anonymousActivity = null;
      this.bookings = null;
      localStorage.removeItem(ADMIN_TOKEN_KEY);
      localStorage.removeItem(ADMIN_EMAIL_KEY);
    },

    // ------------------------------------------------------------------
    // Analytics fetchers
    // ------------------------------------------------------------------
    async fetchOverview() {
      try {
        const { data } = await axios.get('/analytics/overview/', { headers: this.authHeader });
        this.overview = data;
        return data;
      } catch (err) {
        this._handleFetchError(err);
        return null;
      }
    },

    async fetchUsers(days = 7) {
      try {
        const { data } = await axios.get('/analytics/users/', {
          headers: this.authHeader,
          params: { days },
        });
        this.users = data;
        return data;
      } catch (err) {
        this._handleFetchError(err);
        return null;
      }
    },

    async fetchUsersList({ search = '', page = 1, verified = '', ordering = '-date_joined' } = {}) {
      try {
        const params = { page, ordering };
        if (search) params.search = search;
        if (verified !== '') params.verified = verified;
        const { data } = await axios.get('/analytics/users/list/', {
          headers: this.authHeader,
          params,
        });
        this.usersList = data;
        return data;
      } catch (err) {
        this._handleFetchError(err);
        return null;
      }
    },

    async fetchSearches(days = 7) {
      try {
        const { data } = await axios.get('/analytics/searches/', {
          headers: this.authHeader,
          params: { days },
        });
        this.searches = data;
        return data;
      } catch (err) {
        this._handleFetchError(err);
        return null;
      }
    },

    async fetchSystem(days = 7) {
      try {
        const { data } = await axios.get('/analytics/system/', {
          headers: this.authHeader,
          params: { days },
        });
        this.system = data;
        return data;
      } catch (err) {
        this._handleFetchError(err);
        return null;
      }
    },

    async fetchClientErrors(days = 7, errorType = '', page = 1) {
      try {
        const params = { days, page };
        if (errorType) params.error_type = errorType;
        const { data } = await axios.get('/analytics/client-error/', {
          headers: this.authHeader,
          params,
        });
        this.clientErrors = data;
        return data;
      } catch (err) {
        this._handleFetchError(err);
        return null;
      }
    },

    async fetchFeedback(days = 7, category = '', page = 1) {
      try {
        const params = { days, page };
        if (category) params.category = category;
        const { data } = await axios.get('/analytics/feedback/', {
          headers: this.authHeader,
          params,
        });
        this.feedback = data;
        return data;
      } catch (err) {
        this._handleFetchError(err);
        return null;
      }
    },

    async fetchAlerts(days = 7) {
      try {
        const { data } = await axios.get('/analytics/alerts/', {
          headers: this.authHeader,
          params: { days },
        });
        this.alerts = data;
        return data;
      } catch (err) {
        this._handleFetchError(err);
        return null;
      }
    },

    async fetchErrorsGrouped(days = 7, errorType = '') {
      try {
        const params = { days };
        if (errorType) params.error_type = errorType;
        const { data } = await axios.get('/analytics/client-errors/grouped/', {
          headers: this.authHeader,
          params,
        });
        return data;
      } catch (err) {
        this._handleFetchError(err);
        return null;
      }
    },

    async fetchTraffic(days = 7, limit = 200) {
      try {
        const { data } = await axios.get('/analytics/traffic/', {
          headers: this.authHeader,
          params: { days, limit },
        });
        this.traffic = data;
        return data;
      } catch (err) {
        this._handleFetchError(err);
        return null;
      }
    },

    async fetchEngagement(days = 7) {
      try {
        const { data } = await axios.get('/analytics/engagement/', {
          headers: this.authHeader,
          params: { days },
        });
        this.engagement = data;
        return data;
      } catch (err) {
        this._handleFetchError(err);
        return null;
      }
    },

    async fetchPatterns(days = 7) {
      try {
        const { data } = await axios.get('/analytics/patterns/', {
          headers: this.authHeader,
          params: { days },
        });
        this.patterns = data;
        return data;
      } catch (err) {
        this._handleFetchError(err);
        return null;
      }
    },

    async fetchRetention(cohortWeeks = 8) {
      try {
        const { data } = await axios.get('/analytics/retention/', {
          headers: this.authHeader,
          params: { cohort_weeks: cohortWeeks },
        });
        this.retention = data;
        return data;
      } catch (err) {
        this._handleFetchError(err);
        return null;
      }
    },

    async fetchKiwiQuota() {
      try {
        const { data } = await axios.get('/analytics/kiwi-quota/', {
          headers: this.authHeader,
        });
        this.kiwiQuota = data;
        return data;
      } catch (err) {
        this._handleFetchError(err);
        return null;
      }
    },

    async fetchSearchLog(days = 7, page = 1, limit = 50, filter = '') {
      try {
        const params = { days, page, limit };
        if (filter) params.filter = filter;
        const { data } = await axios.get('/analytics/search-log/', {
          headers: this.authHeader,
          params,
        });
        this.searchLog = data;
        return data;
      } catch (err) {
        this._handleFetchError(err);
        return null;
      }
    },

    async fetchAnonymousActivity(days = 7, page = 1, eventType = '', limit = 50) {
      try {
        const params = { days, page, limit };
        if (eventType) params.event_type = eventType;
        const { data } = await axios.get('/analytics/anonymous-activity/', {
          headers: this.authHeader,
          params,
        });
        this.anonymousActivity = data;
        return data;
      } catch (err) {
        this._handleFetchError(err);
        return null;
      }
    },

    async fetchFunnel(days = 7) {
      try {
        const { data } = await axios.get('/analytics/funnel/', {
          headers: this.authHeader,
          params: { days },
        });
        this.funnel = data;
        return data;
      } catch (err) {
        this._handleFetchError(err);
        return null;
      }
    },

    async fetchBookings(days = 7) {
      try {
        const { data } = await axios.get('/analytics/bookings/', {
          headers: this.authHeader,
          params: { days },
        });
        this.bookings = data;
        return data;
      } catch (err) {
        this._handleFetchError(err);
        return null;
      }
    },

    _handleFetchError(err) {
      if (err.response?.status === 403) {
        // Session expired – clear it so the router redirects to login
        this.sessionToken = null;
        this.adminEmail = null;
        localStorage.removeItem(ADMIN_TOKEN_KEY);
        localStorage.removeItem(ADMIN_EMAIL_KEY);
      }
      this.error = err.response?.data?.detail || err.message || 'Unknown error';
    },
  },
});
